import { reportError } from '@/services/error-reporting';
import { NORMAL_CONFIDENTIALITY_LEVEL_CODING, PAYMENT_ATTRIBUTION_PROVENANCE_TAG } from '@bariendo/lib/codings';
import { createReference, MedplumClient, ProfileResource } from '@medplum/core';
import { Provenance } from '@medplum/fhirtypes';

/**
 * Who did what to a payment, recorded in Medplum because nowhere else can say.
 *
 * `bariendo-be` authenticates these writes with a shared Bearer key and cannot
 * tell which Orbit user is calling, so `actingUser` on its request bodies is
 * logged and never persisted (`payment.service.ts`). Medplum *has* an
 * authenticated Practitioner, so attribution lives here — the only place that
 * can vouch for it.
 *
 * The payments DB holds no FHIR resources, so each target is an
 * `identifier` on `Provenance.target` rather than a reference.
 *
 * **Not an audit log.** These are decorations on financial cards. Every write
 * and read below fails soft: money is already recorded by the time we get
 * here, and losing a name must never blank a balance or make an operator
 * re-key a settlement they already entered.
 */

/** A charge in the payments DB — who hand-keyed a Cherry/CareCredit settlement. */
export const CHARGE_IDENTIFIER_SYSTEM = 'https://bariendo.com/payment-charge-id';

/**
 * A checkout session in the payments DB — who created the treatment order.
 *
 * This is the "win" attribution (BAR-2614). Order creation is the one human
 * act present on every sale: a patient paying a Stripe link themselves
 * involves no Orbit user at all, so nobody "completes" the payment, and
 * crediting the creator is the closest equivalent to Close's opportunity owner.
 */
export const CHECKOUT_SESSION_IDENTIFIER_SYSTEM = 'https://bariendo.com/payment-checkout-session-id';

/**
 * Attributes a payments-DB row to the authenticated Practitioner.
 *
 * Never throws. A failure here means the card renders without a name, which is
 * strictly better than telling someone their action failed when the money
 * moved.
 */
export async function writePaymentAttribution(
  medplum: MedplumClient,
  args: {
    system: string;
    /** The payments-DB id being attributed — a charge or checkout session id. */
    value: string;
    profile: ProfileResource | undefined;
    /** For error reporting only. */
    component: string;
  }
): Promise<void> {
  const { system, value, profile, component } = args;

  if (!profile) {
    return;
  }

  try {
    await medplum.createResource<Provenance>({
      resourceType: 'Provenance',
      // PN and PC may write Provenance only under their confidentiality
      // clearance, and PAC only with this tag. Unlabelled, the create is a 403
      // swallowed below, which is how every order went unattributed in prod.
      meta: {
        security: [NORMAL_CONFIDENTIALITY_LEVEL_CODING],
        tag: [PAYMENT_ATTRIBUTION_PROVENANCE_TAG],
      },
      target: [{ identifier: { system, value } }],
      recorded: new Date().toISOString(),
      agent: [{ who: createReference(profile) }],
    });
  } catch (e) {
    reportError(e, { component, extra: { value, reason: 'provenance-write-failed' } });
  }
}

/**
 * Payments-DB id -> the display name of whoever acted on it.
 *
 * Ids with no `Provenance` are simply absent from the map, and that absence is
 * meaningful: for a charge it means a webhook wrote it, and for an order it
 * means the order predates BAR-2614 or came from the legacy Close path. Callers
 * must render the absence rather than inferring a name.
 *
 * **The earliest `Provenance` wins.** Order creation is an upsert server-side,
 * so re-pricing an order writes a second one for the same checkout session —
 * and the person who created the sale should keep the credit rather than losing
 * it to whoever last edited it. Sorted server-side by `recorded` so the choice
 * does not depend on the order results happen to arrive in.
 *
 * Returns an empty map on failure, for the same reason the write is silent.
 */
export async function readPaymentAttribution(
  medplum: MedplumClient,
  system: string,
  values: string[],
  component: string
): Promise<ReadonlyMap<string, string>> {
  const present = values.filter(Boolean);
  if (present.length === 0) {
    return new Map();
  }

  try {
    const provenances = await medplum.searchResources('Provenance', {
      'target:identifier': present.map((value) => `${system}|${value}`).join(','),
      _sort: 'recorded',
    });

    const byValue = new Map<string, string>();
    for (const provenance of provenances as Provenance[]) {
      const value = provenance.target?.[0]?.identifier?.value;
      const who = provenance.agent?.[0]?.who?.display;
      // First write wins — see the note above on re-priced orders.
      if (value && who && !byValue.has(value)) {
        byValue.set(value, who);
      }
    }
    return byValue;
  } catch (e) {
    reportError(e, { component, extra: { reason: 'attribution-read-failed' } });
    return new Map();
  }
}
